import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function Terms() {
  return (
    <div className="min-h-screen bg-[#0D0714]">
      {/* Header */}
      <header className="border-b border-white/10 bg-[#0D0714]/90 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link
            to="/"
            className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </Link>
          <img
            src="/images/logo-dark.svg"
            alt="Sonary"
            className="h-8 w-auto ml-auto"
          />
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-4xl font-bold text-white mb-8" data-testid="terms-title">
          Terms of Service
        </h1>

        <div className="prose prose-invert max-w-none space-y-8">
          <p className="text-white/70 text-lg">
            Last updated: December 2025
          </p>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-white">1. Acceptance of Terms</h2>
            <p className="text-white/70 leading-relaxed">
              By accessing or using Sonary ("the Service"), you agree to be bound by these Terms of Service. 
              If you do not agree to these terms, please do not use the Service. We reserve the right to 
              modify these terms at any time, and your continued use of the Service constitutes acceptance 
              of any changes.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-white">2. Description of Service</h2>
            <p className="text-white/70 leading-relaxed">
              Sonary is a social platform that allows users to discover, log, rate, and review music albums. 
              Users can create personal lists, follow other users, and engage with the community through 
              likes and comments. The Service integrates with third-party music databases to provide 
              comprehensive album information.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-white">3. User Accounts</h2>
            <p className="text-white/70 leading-relaxed">
              To access certain features of the Service, you must create an account. You are responsible 
              for maintaining the confidentiality of your account credentials and for all activities that 
              occur under your account. You must provide accurate and complete information when creating 
              your account and keep this information up to date.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-white">4. User Content</h2>
            <p className="text-white/70 leading-relaxed">
              You retain ownership of any content you submit, post, or display on the Service ("User Content"). 
              By posting User Content, you grant Sonary a non-exclusive, worldwide, royalty-free license to 
              use, display, and distribute your content in connection with the Service. You are solely 
              responsible for your User Content and the consequences of posting it.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-white">5. Acceptable Use</h2>
            <p className="text-white/70 leading-relaxed">
              You agree not to use the Service to:
            </p>
            <ul className="list-disc list-inside text-white/70 space-y-2 ml-4">
              <li>Post content that is unlawful, harmful, threatening, abusive, or otherwise objectionable</li>
              <li>Impersonate any person or entity or misrepresent your affiliation</li>
              <li>Harass, bully, or intimidate other users</li>
              <li>Spam or send unsolicited communications</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Attempt to gain unauthorized access to any part of the Service</li>
              <li>Use the Service for any illegal purpose</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-white">6. Intellectual Property</h2>
            <p className="text-white/70 leading-relaxed">
              The Service and its original content (excluding User Content), features, and functionality 
              are owned by Sonary and are protected by international copyright, trademark, and other 
              intellectual property laws. Album artwork, metadata, and related information are provided 
              by third-party services and remain the property of their respective owners.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-white">7. Third-Party Services</h2>
            <p className="text-white/70 leading-relaxed">
              The Service may contain links to or integrate with third-party websites or services. We are 
              not responsible for the content, privacy policies, or practices of any third-party services. 
              Your use of such services is at your own risk and subject to their respective terms.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-white">8. Termination</h2>
            <p className="text-white/70 leading-relaxed">
              We may terminate or suspend your account and access to the Service immediately, without 
              prior notice, for conduct that we believe violates these Terms or is harmful to other 
              users, us, or third parties. Upon termination, your right to use the Service will 
              immediately cease.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-white">9. Disclaimer of Warranties</h2>
            <p className="text-white/70 leading-relaxed">
              The Service is provided "as is" and "as available" without warranties of any kind, either 
              express or implied. We do not warrant that the Service will be uninterrupted, error-free, 
              or completely secure. Your use of the Service is at your sole risk.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-white">10. Limitation of Liability</h2>
            <p className="text-white/70 leading-relaxed">
              To the maximum extent permitted by law, Sonary shall not be liable for any indirect, 
              incidental, special, consequential, or punitive damages, or any loss of profits or 
              revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, 
              or other intangible losses resulting from your use of the Service.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-white">11. Changes to Terms</h2>
            <p className="text-white/70 leading-relaxed">
              We reserve the right to modify these Terms at any time. We will notify users of any 
              material changes by posting the new Terms on this page and updating the "Last updated" 
              date. Your continued use of the Service after such changes constitutes acceptance of 
              the new Terms.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-white">12. Contact Us</h2>
            <p className="text-white/70 leading-relaxed">
              If you have any questions about these Terms of Service, please contact us through the 
              application or visit our support channels.
            </p>
          </section>
        </div>

        {/* Footer Links */}
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-wrap gap-6 text-sm">
          <Link to="/privacy" className="text-purple-400 hover:text-purple-300 transition-colors">
            Privacy Policy
          </Link>
          <Link to="/" className="text-white/50 hover:text-white transition-colors">
            Back to Home
          </Link>
        </div>
      </main>
    </div>
  );
}
